import React, { useEffect, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title,
  Text,
  Stack,
  Paper,
  Table,
  Badge,
  Button,
  Group,
  Box,
  ActionIcon,
  Tooltip,
  Alert,
  Skeleton
} from '@mantine/core';
import {
  IconPlus,
  IconEye,
  IconMapPin,
  IconCircleCheck,
  IconCircleX,
  IconAlertTriangle
} from '@tabler/icons-react';
import { useAuth } from '../../hooks/useAuth';
import { useRegionStore } from '../../../application/store/regionStore';
import { RegionListItemDto } from '../../../domain/entities/Region';
import { buildRegionDetailRoute } from '../../routing/routes';
import { CreateRegionModal } from '../../components/domain/CreateRegionModal';

/**
 * RegionsPage - Admin-only page for managing regions
 *
 * Features:
 * - List all regions with SubArea counts
 * - View region details
 * - Create new region
 * - View SubAreas for each region
 * - Toggle region active status
 *
 * Security:
 * - Admin-only access enforced by route protection
 * - Backend enforces all authorization
 */
export const RegionsPage: React.FC = (): React.ReactElement => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { regions, isLoading, error, fetchRegions } = useRegionStore();

  // Modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);

  /**
   * Fetch regions on mount
   */
  useEffect(() => {
    const loadRegions = async (): Promise<void> => {
      if (user?.role === 'Admin') {
        await fetchRegions(undefined, user.role);
      }
    };

    void loadRegions();
  }, [user, fetchRegions]);

  /**
   * Navigation handlers
   */
  const handleViewRegion = useCallback(
    (region: RegionListItemDto): void => {
      navigate(buildRegionDetailRoute(region.id));
    },
    [navigate]
  );

  /**
   * Open create region modal
   */
  const handleCreateRegion = useCallback((): void => {
    setIsCreateModalOpen(true);
  }, []);

  /**
   * Handle successful region creation - refresh list and close modal
   */
  const handleCreateSuccess = useCallback(async (): Promise<void> => {
    await fetchRegions(undefined, user?.role ?? 'BusinessOwner');
    setIsCreateModalOpen(false);
  }, [fetchRegions, user?.role]);

  /**
   * Render loading skeleton
   */
  if (isLoading && regions.length === 0) {
    return (
      <Stack gap="lg">
        <Group justify="space-between">
          <Box>
            <Skeleton height={36} width={200} mb="xs" />
            <Skeleton height={20} width={300} />
          </Box>
          <Skeleton height={36} width={120} />
        </Group>

        <Paper shadow="sm" p="md" radius="md" withBorder>
          <Stack gap="xs">
            <Skeleton height={40} />
            <Skeleton height={60} />
            <Skeleton height={60} />
            <Skeleton height={60} />
          </Stack>
        </Paper>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Box>
          <Title order={1} ff="Comfortaa, sans-serif">
            Regions
          </Title>
          <Text c="dimmed" size="lg" mt="xs">
            Manage geographic regions and their SubAreas
          </Text>
        </Box>

        <Button leftSection={<IconPlus size={16} />} onClick={handleCreateRegion}>
          Create Region
        </Button>
      </Group>

      {/* Error Alert */}
      {error && (
        <Alert icon={<IconAlertTriangle />} title="Error Loading Regions" color="red" variant="light">
          {error}
        </Alert>
      )}

      {/* Regions Table */}
      {!error && regions.length > 0 && (
        <Paper shadow="sm" p="md" radius="md" withBorder>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Region Name</Table.Th>
                <Table.Th>Location</Table.Th>
                <Table.Th>SubAreas</Table.Th>
                <Table.Th>Subreddits</Table.Th>
                <Table.Th>Businesses</Table.Th>
                <Table.Th>Mentions</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {regions.map((region) => (
                <Table.Tr key={region.id}>
                  {/* Region Name */}
                  <Table.Td>
                    <Group gap="xs">
                      <IconMapPin size={16} color="var(--mantine-color-gray-6)" />
                      <Text fw={600}>{region.name}</Text>
                    </Group>
                    {region.description && (
                      <Text size="xs" c="dimmed" mt={4}>
                        {region.description}
                      </Text>
                    )}
                  </Table.Td>

                  {/* Location */}
                  <Table.Td>
                    <Text size="sm">
                      {[region.city, region.state, region.country].filter(Boolean).join(', ')}
                    </Text>
                  </Table.Td>

                  {/* SubAreas Count */}
                  <Table.Td>
                    <Badge variant="light" color={region.subAreaCount === 0 ? 'red' : 'blue'}>
                      {region.subAreaCount} SubArea{region.subAreaCount !== 1 ? 's' : ''}
                    </Badge>
                  </Table.Td>

                  {/* Subreddits */}
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {region.monitoredSubreddits.length} subreddit{region.monitoredSubreddits.length !== 1 ? 's' : ''}
                    </Text>
                  </Table.Td>

                  {/* Businesses */}
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {region.businessCount}
                    </Text>
                  </Table.Td>

                  {/* Mentions */}
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {region.mentionCount}
                    </Text>
                  </Table.Td>

                  {/* Status */}
                  <Table.Td>
                    {region.isActive ? (
                      <Badge color="green" variant="light" leftSection={<IconCircleCheck size={14} />}>
                        Active
                      </Badge>
                    ) : (
                      <Badge color="gray" variant="light" leftSection={<IconCircleX size={14} />}>
                        Inactive
                      </Badge>
                    )}
                  </Table.Td>

                  {/* Actions */}
                  <Table.Td>
                    <Group gap="xs">
                      <Tooltip label="View Details">
                        <ActionIcon variant="light" color="blue" onClick={() => handleViewRegion(region)}>
                          <IconEye size={18} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      {/* Empty State */}
      {!error && !isLoading && regions.length === 0 && (
        <Paper shadow="sm" p="xl" radius="md" withBorder>
          <Stack align="center" gap="md">
            <IconMapPin size={48} stroke={1.5} color="var(--mantine-color-gray-5)" />
            <Box ta="center">
              <Text size="lg" fw={600} mb="xs">
                No Regions Found
              </Text>
              <Text size="sm" c="dimmed" mb="md">
                Create your first region to start monitoring mentions in specific geographic areas.
              </Text>
              <Button leftSection={<IconPlus size={16} />} onClick={handleCreateRegion}>
                Create First Region
              </Button>
            </Box>
          </Stack>
        </Paper>
      )}

      {/* Create Region Modal */}
      <CreateRegionModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
      />
    </Stack>
  );
};
